import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Zap, Smartphone, Tv, Droplets, Landmark, Receipt, Loader2, CheckCircle2, Copy, MessageSquare, Gift, ChevronLeft, AlertTriangle, Clock, RotateCcw } from "lucide-react";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { BundlePicker } from "@/components/BundlePicker";

const ICONS = { airtime: Smartphone, electricity: Zap, paytv: Tv, water: Droplets, tax: Landmark, ers: Smartphone };
const LABEL = { airtime: "Phone number to top up", electricity: "Electricity meter number", paytv: "Decoder / smartcard number", water: "Water customer number", tax: "RRA document / reference number" };
const fmt = (n) => `${Number(n || 0).toLocaleString()} RWF`;
const hrs = (h) => (h >= 48 && h % 24 === 0 ? `${h / 24} days` : `${h} hours`);

// Mirrors backend reward_hours_for: base hours, upgraded by the highest tier the amount reaches.
const rewardFor = (cat, amount) => {
  const base = cat?.rewardHours ?? 48;
  if (!base || !amount) return { hours: 0, next: null };
  const tiers = [...(cat?.rewardTiers || [])].sort((a, b) => a.min - b.min);
  const hours = tiers.reduce((h, t) => (amount >= t.min ? Math.max(h, t.hours) : h), base);
  return { hours, next: tiers.find((t) => amount < t.min && t.hours > hours) || null };
};

const RewardPreview = ({ cat, amount }) => {
  const { hours, next } = rewardFor(cat, amount);
  if (!hours) return null;
  return (
    <div className="rounded-xl bg-[#E11D2A]/10 border border-[#E11D2A]/40 px-3 py-2.5 text-sm text-white flex items-start gap-2" data-testid="util-reward-preview">
      <Gift className="w-4 h-4 text-[#E11D2A] mt-0.5 shrink-0" />
      <div><b data-testid="util-reward-preview-hours">This purchase unlocks {hrs(hours)} FREE</b> Live Radio, Live TV & premium videos.
        {next && <div className="text-xs text-white mt-0.5" data-testid="util-reward-preview-next">Add {fmt(next.min - amount)} more to get {hrs(next.hours)}.</div>}</div>
    </div>
  );
};
// Mirrors backend utility_fees.rra_charge: [from, to, charge, agent]
const rraCharge = (cat, amount) => {
  const row = (cat?.rraTable || []).find(([lo, hi]) => amount >= lo && (hi == null || amount <= hi));
  return row ? row[2] : 0;
};
const FeeBreakdown = ({ amount, charge }) => (
  <div className="rounded-xl border-2 border-white/30 bg-black p-4 text-base" data-testid="util-fee-breakdown">
    <div className="flex justify-between text-white"><span>RRA payment</span><b data-testid="util-fee-original">{fmt(amount)}</b></div>
    <div className="flex justify-between text-white mt-1"><span>Service charge</span><b data-testid="util-fee-charge">{fmt(charge)}</b></div>
    <div className="flex justify-between text-white mt-2 pt-2 border-t border-white/25 text-lg"><span className="font-bold">Total to pay</span><b data-testid="util-fee-total">{fmt(amount + charge)}</b></div>
  </div>
);
const err = (e) => apiError(e?.response?.data?.detail) || "Something went wrong. Please try again.";

const Steps = ({ step }) => (
  <div className="flex items-center gap-2 mb-6" data-testid="util-steps">
    {["Details", "Pay", "Receipt"].map((s, i) => (
      <div key={s} className="flex-1">
        <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-[#E11D2A]" : "bg-[#273042]"}`} />
        <div className={`text-[11px] mt-1.5 font-semibold ${i <= step ? "text-white" : "text-white"}`}>{i + 1}. {s}</div>
      </div>
    ))}
  </div>
);

export const RewardCountdown = ({ until, className = "" }) => {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 60000); return () => clearInterval(t); }, []);
  const ms = until ? new Date(until) - new Date() : 0;
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600e3), m = Math.floor((ms % 3600e3) / 60e3);
  return (
    <div className={`inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-400/40 text-emerald-200 text-xs font-bold px-3 py-1.5 ${className}`} data-testid="utility-reward-countdown">
      <Gift className="w-3.5 h-3.5" /> Free premium access · {h}h {m}m left
    </div>
  );
};

const ReceiptCard = ({ o, onSms }) => {
  const rc = o.receipt || {};
  const copy = () => { navigator.clipboard?.writeText(rc.voucher || ""); toast.success("Token copied"); };
  return (
    <div className="rounded-2xl border border-emerald-500/40 bg-[#0B1A14] p-5" data-testid="util-receipt">
      <div className="flex items-center gap-2 text-emerald-300 font-bold"><CheckCircle2 className="w-5 h-5" /> Purchase successful</div>
      <div className="mt-3 text-white text-lg font-extrabold">{o.productName || o.verticalName} · {fmt(o.amount)}</div>
      <div className="text-sm text-slate-100">For {o.account}{rc.customerAccountName || o.customerName ? ` · ${rc.customerAccountName || o.customerName}` : ""}</div>
      {rc.voucher && (
        <div className="mt-4 rounded-xl bg-black/40 border border-white/25 p-4">
          <div className="text-[11px] uppercase tracking-wider text-white font-bold">Token</div>
          <div className="text-2xl sm:text-3xl font-mono font-extrabold text-white tracking-wider break-all" data-testid="util-receipt-token">{rc.voucher}</div>
          {rc.units && <div className="text-sm text-white mt-1" data-testid="util-receipt-units">Units: {rc.units}</div>}
        </div>
      )}
      <div className="mt-3 text-xs text-slate-100 space-y-0.5">
        {rc.receiptNo && <div data-testid="util-receipt-no">Receipt no: <span className="text-white font-mono">{rc.receiptNo}</span></div>}
        {rc.spName && <div>Provider: {rc.spName}</div>}
        {o.completedAt && <div>{new Date(o.completedAt).toLocaleString()}</div>}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {rc.voucher && <button onClick={copy} data-testid="util-copy-token" className="h-10 px-4 rounded-full bg-white text-black text-sm font-bold inline-flex items-center gap-1.5"><Copy className="w-4 h-4" /> Copy token</button>}
        <button onClick={onSms} data-testid="util-sms-again" className="h-10 px-4 rounded-full border border-white/25 text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-white/5"><MessageSquare className="w-4 h-4" /> SMS me again</button>
      </div>
      {o.rewardUntil && <div className="mt-4 rounded-xl bg-[#E11D2A]/10 border border-[#E11D2A]/40 p-3 text-sm text-white font-bold flex items-center gap-2" data-testid="util-reward-badge"><Gift className="w-4 h-4 text-[#E11D2A]" /> Unlocked: {o.rewardHours || 48} hours free access to Live Radio, Live TV & premium videos</div>}
    </div>
  );
};

const STATUS = { paid: "Payment received → buying…", vending: "Payment received → buying…", checking: "Confirming with provider…", successful: "Successful", refund_needed: "Failed — refund in progress", refunded: "Refunded", payment_failed: "Payment not completed" };

const MyPurchases = ({ onOpen, onBuyAgain }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { endpoints.utilMy().then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loader2 className="w-5 h-5 animate-spin text-[#E11D2A]" />;
  if (!rows.length) return <div className="text-sm text-slate-100" data-testid="util-my-empty">No purchases yet.</div>;
  return (
    <div className="space-y-2" data-testid="util-my-list">
      {rows.map((o) => (
        <div key={o.id} className="rounded-xl border border-white/25 bg-black hover:border-[#E11D2A]/50 p-3 flex items-center gap-3 transition-colors">
          <button onClick={() => onOpen(o)} data-testid={`util-my-${o.id}`} className="flex-1 min-w-0 text-left flex items-center gap-3">
            <Receipt className="w-5 h-5 text-[#E11D2A] shrink-0" />
            <div className="flex-1 min-w-0"><div className="text-white text-sm font-bold truncate">{o.verticalName} · {fmt(o.amount)}</div>
              <div className="text-xs text-slate-100 truncate">{o.account} · {new Date(o.createdAt).toLocaleString()}</div></div>
            <span className={`text-[11px] font-bold px-2 py-1 rounded-full shrink-0 ${o.status === "successful" ? "bg-emerald-500/15 text-emerald-300" : o.status === "refund_needed" ? "bg-amber-500/15 text-amber-300" : "bg-white/5 text-white"}`}>{STATUS[o.status] || o.status}</span>
          </button>
          <button onClick={() => onBuyAgain(o)} data-testid={`util-buy-again-${o.id}`} className="h-9 px-3 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white text-xs font-bold inline-flex items-center gap-1 shrink-0 transition-colors"><RotateCcw className="w-3.5 h-3.5" /> Buy again</button>
        </div>
      ))}
    </div>
  );
};

const CardPay = ({ quote, amount: typed, extra = 0, bundleId, onPaid }) => {
  const [q, setQ] = useState(null);
  const [amount, setAmount] = useState(typed);
  const total = Number(amount) + extra;
  const utilOrder = useRef(null);
  useEffect(() => { const t = setTimeout(() => setAmount(typed), 500); return () => clearTimeout(t); }, [typed]);
  useEffect(() => { endpoints.utilCardQuote(total).then((r) => setQ(r.data)).catch(() => setQ({ enabled: false })); }, [total]);
  if (!q) return <Loader2 className="w-5 h-5 animate-spin text-[#E11D2A]" />;
  if (!q.enabled) return <div className="text-sm text-slate-100" data-testid="util-card-unavailable">Card payments are not available right now. Please use MoMo.</div>;
  const create = async (source) => {
    const { data } = await endpoints.utilPaypal({ quoteId: quote.quoteId, amount: Number(amount), source, bundleId });
    utilOrder.current = data.utilityOrderId;
    return data.orderId;
  };
  return (
    <div data-testid="util-card-pay">
      <div className="text-sm text-white">{fmt(total)} ≈ <b className="text-white" data-testid="util-card-usd">${q.usd.toFixed(2)}</b> charged in USD (includes card fees).</div>
      <PayPalCheckout key={`${quote.quoteId}-${amount}-${bundleId || ""}`} plan={{ id: `utility-${amount}` }} currency="USD" amountLabel={`$${q.usd.toFixed(2)}`} createOrderFn={create} onSuccess={() => onPaid(utilOrder.current)} />
    </div>
  );
};

export default function UtilitiesPage() {
  const { user, isAuthed, refresh } = useAuth();
  const [cat, setCat] = useState(null);
  const [tab, setTab] = useState("buy");
  const [svc, setSvc] = useState(null);
  const [acct, setAcct] = useState("");
  const [quote, setQuote] = useState(null);
  const [amount, setAmount] = useState("");
  const [bundle, setBundle] = useState(null);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [method, setMethod] = useState("momo");
  const [again, setAgain] = useState(null);
  const poll = useRef(null);

  useEffect(() => { endpoints.utilCatalog().then((r) => setCat(r.data)).catch((e) => setCat({ enabled: false, error: err(e) })); }, []);
  useEffect(() => { setPhone((user?.phone || "").replace(/^\+?250/, "")); }, [user]);
  useEffect(() => () => clearInterval(poll.current), []);

  const reset = () => { clearInterval(poll.current); setSvc(null); setQuote(null); setAcct(""); setAmount(""); setOrder(null); };
  const needAuth = () => { if (isAuthed) return false; window.dispatchEvent(new Event("open-auth")); return true; };

  const check = async (v = svc, a = acct, preset) => {
    if (needAuth()) return;
    setBusy(true);
    try {
      const { data } = await endpoints.utilValidate({ verticalId: v.id, account: a.trim() });
      setQuote(data);
      setBundle(null);
      setAmount(data.bundles?.length ? "" : preset || data.amounts?.[0] || data.packages?.[0]?.amount || "");
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  // One tap from My purchases: same service + same meter/number, previous amount pre-filled.
  useEffect(() => {
    if (!again) return;
    setAgain(null);
    check(again.svc, again.account, again.amount);
  }, [again]); // eslint-disable-line react-hooks/exhaustive-deps
  const buyAgain = (o) => {
    clearInterval(poll.current);
    const v = cat?.verticals?.find((x) => x.id === o.verticalId) || { id: o.verticalId, name: o.verticalName };
    setTab("buy"); setOrder(null); setQuote(null); setSvc(v); setAcct(o.account);
    setAgain({ svc: v, account: o.account, amount: o.amount });
  };

  const track = (id) => {
    clearInterval(poll.current);
    const tickOnce = async () => {
      try {
        const { data } = await endpoints.utilOrderGet(id);
        setOrder(data);
        if (["successful", "refund_needed", "refunded", "payment_failed"].includes(data.status)) {
          clearInterval(poll.current);
          if (data.status === "successful") { refresh(); toast.success(data.rewardUntil ? `Done! ${data.rewardHours || 48} hours of free access unlocked` : "Purchase successful"); }
        }
      } catch { /* keep polling */ }
    };
    tickOnce();
    poll.current = setInterval(tickOnce, 4000);
  };

  const pay = async () => {
    setBusy(true);
    try { const { data } = await endpoints.utilOrder({ quoteId: quote.quoteId, amount: Number(amount), momoPhone: phone, bundleId: bundle?.id }); setOrder({ id: data.orderId, status: "awaiting_payment", amount: Number(amount), momoPhone: data.momoPhone }); track(data.orderId); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const sms = async () => { try { await endpoints.utilSms(order.id); toast.success("Receipt sent by SMS"); } catch (e) { toast.error(err(e)); } };
  const step = order ? (order.status === "successful" ? 2 : 1) : 0;
  const choices = [...(quote?.amounts || []).map((a) => ({ amount: a, name: fmt(a) })), ...(quote?.packages || []).filter((p) => p.amount).map((p) => ({ amount: p.amount, name: `${p.name} · ${fmt(p.amount)}` }))];
  const hasBundles = quote?.bundles?.length > 0;
  const fixed = hasBundles || (quote?.vendUnitId === "fixed" && choices.length > 0);
  const charge = svc?.id === "tax" && Number(amount) > 0 ? rraCharge(cat, Number(amount)) : 0;
  const total = Number(amount || 0) + charge;

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-10" data-testid="utilities-page">
      <h1 className="text-4xl sm:text-5xl font-extrabold text-white">Utilities</h1>
      <p className="text-white mt-2 text-base">Airtime, bundles, electricity tokens, TV, RRA taxes and water — pay with MTN MoMo, card or PayPal.</p>
      <div className="mt-4 rounded-2xl border border-[#E11D2A]/40 bg-gradient-to-r from-[#E11D2A]/15 to-transparent p-4 flex items-center gap-3" data-testid="util-reward-promo">
        <Gift className="w-6 h-6 text-[#E11D2A] shrink-0" />
        <div className="text-sm text-white"><b>Every successful purchase = {cat?.rewardTiers?.length ? "from " : ""}{cat?.rewardHours || 48} hours FREE</b> Live Radio, Live TV & premium videos.
          {cat?.rewardTiers?.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="util-reward-tiers">{cat.rewardTiers.map((t) => (
            <span key={t.min} className="text-[11px] font-bold rounded-full bg-black/40 border border-[#E11D2A]/40 px-2 py-0.5">{fmt(t.min)}+ → {t.hours >= 48 && t.hours % 24 === 0 ? `${t.hours / 24} days` : `${t.hours}h`} free</span>))}</div>}</div>
      </div>
      {user?.utilityAccessUntil && <RewardCountdown until={user.utilityAccessUntil} className="mt-3" />}

      <div className="flex gap-2 mt-6 mb-5">
        {[["buy", "Buy"], ["my", "My purchases"]].map(([k, l]) => (
          <button key={k} onClick={() => { if (k === "my" && needAuth()) return; setTab(k); }} data-testid={`util-tab-${k}`}
            className={`h-10 px-5 rounded-full text-sm font-bold border transition-colors ${tab === k ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "border-white/25 text-white hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "my" ? <MyPurchases onBuyAgain={buyAgain} onOpen={(o) => { setTab("buy"); setOrder(o); setSvc({ id: o.verticalId, name: o.verticalName }); }} /> : !cat ? <Loader2 className="w-6 h-6 animate-spin text-[#E11D2A]" /> : !cat.enabled ? (
        <div className="rounded-2xl border border-white/25 bg-black p-6 text-white text-sm" data-testid="util-unavailable">Utilities are not available right now. Please check back soon.</div>
      ) : !svc ? (
        <div className="grid grid-cols-2 gap-3" data-testid="util-services">
          {cat.verticals.map((v) => { const I = ICONS[v.id] || Receipt; return (
            <button key={v.id} onClick={() => setSvc(v)} data-testid={`util-service-${v.id}`} className="rounded-2xl border border-white/25 bg-black hover:border-[#E11D2A]/60 hover:-translate-y-0.5 transition-[transform,border-color] p-5 text-left min-h-[120px]">
              <I className="w-8 h-8 text-[#E11D2A]" /><div className="text-white font-bold mt-3">{v.name}</div>
            </button>); })}
        </div>
      ) : (
        <div className="rounded-2xl border border-white/25 bg-black p-5 sm:p-6">
          <button onClick={reset} className="text-xs text-slate-100 hover:text-white inline-flex items-center gap-1 mb-4" data-testid="util-back"><ChevronLeft className="w-4 h-4" /> All services</button>
          <Steps step={step} />
          <div className="text-white font-bold text-lg mb-4">{svc.name}</div>
          {!order && (
            <>
              <label className="block text-xs uppercase tracking-wider text-slate-100 font-semibold">{LABEL[svc.id] || "Account number"}</label>
              <div className="flex gap-2 mt-1.5">
                <input value={acct} onChange={(e) => { setAcct(e.target.value); setQuote(null); }} inputMode="numeric" data-testid="util-account-input"
                  className="flex-1 min-w-0 h-12 px-4 rounded-xl bg-black border border-white/25 text-white text-lg outline-none focus:border-[#E11D2A]/60" placeholder={svc.id === "airtime" ? "078 xxx xxxx" : ""} />
                <button onClick={() => check()} disabled={busy || acct.trim().length < 4} data-testid="util-check-btn" className="shrink-0 h-12 px-5 rounded-xl bg-white text-black font-bold disabled:opacity-50">{busy && !quote ? <Loader2 className="w-5 h-5 animate-spin" /> : "Check"}</button>
              </div>
              {quote && (
                <div className="mt-5 space-y-4" data-testid="util-quote">
                  <div className="rounded-xl bg-black border border-white/25 p-4 text-sm">
                    {quote.customerName && <div className="text-white font-bold" data-testid="util-customer-name">Customer: {quote.customerName}</div>}
                    <div className="text-slate-100">{quote.productName || quote.provider}{quote.provider && quote.productName ? ` — ${quote.provider}` : ""}</div>
                    {!fixed && (quote.vendMin || quote.vendMax) ? <div className="text-white text-xs mt-1">Amount {fmt(quote.vendMin)} – {fmt(quote.vendMax)}</div> : null}
                  </div>
                  {choices.length > 0 && <div className="flex flex-wrap gap-2">{choices.map((c) => (
                    <button key={`${c.name}-${c.amount}`} onClick={() => setAmount(c.amount)} data-testid={`util-amount-${c.amount}`}
                      className={`h-10 px-4 rounded-full text-sm font-bold border ${Number(amount) === Number(c.amount) ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "border-white/25 text-white"}`}>{c.name}</button>))}</div>}
                  {hasBundles && <BundlePicker bundles={quote.bundles} value={bundle?.id} onPick={(b) => { setBundle(b); setAmount(b.price); }} />}
                  {!fixed && <div><label className="block text-xs uppercase tracking-wider text-slate-100 font-semibold">Amount (RWF)</label>
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="util-amount-input" min={quote.vendMin || 1} max={quote.vendMax || undefined}
                      className="mt-1.5 w-full h-12 px-4 rounded-xl bg-black border border-white/25 text-white text-lg outline-none focus:border-[#E11D2A]/60" /></div>}
                  {svc.id === "tax" && Number(amount) > 0 && <FeeBreakdown amount={Number(amount)} charge={charge} />}
                  <RewardPreview cat={cat} amount={Number(amount)} />
                  <div className="grid grid-cols-2 gap-2" data-testid="util-method">
                    {[["momo", "MTN MoMo"], ["card", "Visa card / PayPal"]].map(([k, l]) => (
                      <button key={k} onClick={() => setMethod(k)} data-testid={`util-method-${k}`}
                        className={`h-11 rounded-xl text-sm font-bold border transition-colors ${method === k ? "bg-white text-black border-white" : "border-white/25 text-white hover:text-white"}`}>{l}</button>))}
                  </div>
                  {method === "card" ? (Number(amount) ? <CardPay quote={quote} amount={amount} extra={charge} bundleId={bundle?.id} onPaid={(id) => { setOrder({ id, status: "awaiting_payment", amount: Number(amount) }); track(id); }} /> : <div className="text-sm text-slate-100">{hasBundles ? "Choose a bundle first." : "Enter an amount first."}</div>) : (<>
                  <div><label className="block text-xs uppercase tracking-wider text-slate-100 font-semibold">MTN MoMo number</label>
                    <div className="mt-1.5 flex"><span className="h-12 px-3 rounded-l-xl bg-black border border-r-0 border-white/25 text-white flex items-center">+250</span>
                      <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" data-testid="util-momo-input" placeholder="788 123 456"
                        className="flex-1 min-w-0 h-12 px-4 rounded-r-xl bg-black border border-white/25 text-white text-lg outline-none focus:border-[#E11D2A]/60" /></div></div>
                  <button onClick={pay} disabled={busy || !Number(amount)} data-testid="util-pay-btn" className="w-full h-12 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white font-extrabold transition-colors disabled:opacity-50">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : Number(amount) ? `Pay ${fmt(total)} with MTN MoMo` : "Enter an amount"}</button>
                  </>)}
                </div>
              )}
            </>
          )}
          {order && order.status === "awaiting_payment" && (
            <div className="text-center py-6" data-testid="util-awaiting"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A] mx-auto" />
              <div className="text-white font-bold mt-3">Approve the MoMo prompt on {order.momoPhone}</div><div className="text-sm text-slate-100 mt-1">Dial *182*7*1# if no prompt appears.</div>
              {order.createdAt && Date.now() - new Date(order.createdAt).getTime() > 180000 && <div className="text-sm text-amber-200 mt-3 max-w-sm mx-auto" data-testid="util-awaiting-long">Still waiting for MTN to confirm. If you already approved, don't pay again — we keep checking and will deliver automatically as soon as it's confirmed.</div>}</div>
          )}
          {order && ["paid", "vending", "checking"].includes(order.status) && (
            <div className="text-center py-6" data-testid="util-vending"><Clock className="w-8 h-8 text-amber-300 mx-auto animate-pulse" /><div className="text-white font-bold mt-3">{STATUS[order.status]}</div><div className="text-sm text-slate-100 mt-1">This usually takes a few seconds.</div></div>
          )}
          {order?.status === "successful" && <ReceiptCard o={order} onSms={sms} />}
          {order && ["refund_needed", "refunded", "payment_failed"].includes(order.status) && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/40 p-4 text-sm text-amber-100" data-testid="util-failed"><AlertTriangle className="w-5 h-5 inline mr-1.5" />
              {order.status === "payment_failed" ? "The MoMo payment was not completed. You were not charged." : order.status === "refunded" ? "This purchase failed and your money has been refunded." : "The purchase could not be completed. Our team has been alerted and will refund your MoMo payment."}
              <button onClick={reset} className="block mt-3 underline" data-testid="util-try-again">Try again</button></div>
          )}
        </div>
      )}
    </div>
  );
}
